import React from 'react';
import clsx from 'clsx';
import type { TentConfigV4 } from '../types';
import { formatPrice } from '../../../state/offerings';
import { useBrandTheme } from '../brand-theme';

interface RoomCardProps {
  tentConfig: TentConfigV4;
  quantity: number;
  pricePerNight: number;
  isAvailable: boolean;
  onQuantityChange: (delta: number) => void;
  onViewDetails: () => void;
  messages: {
    pricePerNight: string;
    viewDetails: string;
    capacity: string;
    mealsIncluded: string;
    privateBathroom: string;
    sharedBathroom: string;
    unavailable: string;
  };
}

/**
 * Visual room card with large image, details, and quantity selector.
 * Follows Airbnb/Booking.com card patterns.
 * Uses brand theme colors from context for selection states.
 */
export const RoomCard: React.FC<RoomCardProps> = ({
  tentConfig,
  quantity,
  pricePerNight,
  isAvailable,
  onQuantityChange,
  onViewDetails,
  messages,
}) => {
  const { theme } = useBrandTheme();
  const isSelected = quantity > 0;

  return (
    <div
      className={clsx(
        'relative rounded-lg overflow-hidden',
        'border-2 transition-all duration-200',
        'bg-white',
        {
          [`${theme.accentBorder} shadow-md-tent`]: isSelected,
          'border-gray-200 hover:border-gray-300 hover:shadow-md-tent': !isSelected && isAvailable,
          'border-gray-100 opacity-60': !isAvailable,
        },
      )}
    >
      {/* Image section - 60% of card on desktop */}
      <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden">
        <img
          src={tentConfig.imageUrl}
          alt={tentConfig.imageAlt}
          className={clsx(
            'w-full h-full object-cover',
            'transition-transform duration-300',
            isAvailable && 'group-hover:scale-105',
          )}
        />

        {/* Unavailable overlay */}
        {!isAvailable && (
          <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
            <span className="text-white font-serif font-semibold text-lg px-4 py-2 bg-gray-900/70 rounded-lg">
              {messages.unavailable}
            </span>
          </div>
        )}

        {/* Selected badge */}
        {isSelected && (
          <div className="absolute top-3 left-3">
            <span className={clsx('inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-sm font-serif font-semibold rounded-md shadow-md', theme.accentBadge)}>
              <CheckIcon className="w-4 h-4" />
              {quantity} selected
            </span>
          </div>
        )}

        {/* View details button */}
        {isAvailable && (
          <button
            type="button"
            onClick={onViewDetails}
            className={clsx(
              'absolute top-3 right-3',
              'px-3 py-1.5 bg-white/90 backdrop-blur-sm',
              'text-sm font-medium text-gray-700',
              'rounded-md shadow-md',
              'hover:bg-white transition-colors',
            )}
          >
            {messages.viewDetails}
          </button>
        )}
      </div>

      {/* Content section */}
      <div className="p-4 md:p-5">
        {/* Title and price row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-lg font-serif font-semibold text-gray-700">
              {tentConfig.displayName || tentConfig.title}
            </h3>
            {tentConfig.capacity && (
              <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                <UsersIcon className="w-4 h-4" />
                {messages.capacity}: {tentConfig.capacity}
              </p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-lg font-mono font-bold text-gray-700">
              {formatPrice({ amount: pricePerNight, currency: 'INR' })}
            </p>
            <p className="text-xs text-gray-500">{messages.pricePerNight}</p>
          </div>
        </div>

        {/* Key amenities - show top 3 */}
        <div className="flex flex-wrap gap-2 mb-4">
          {/* Bathroom type badge */}
          <span
            className={clsx(
              'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium',
              tentConfig.hasPrivateBathroom
                ? `${theme.accentBgLight} ${theme.accentText}`
                : 'bg-gray-100 text-gray-600',
            )}
          >
            <BathIcon className="w-3.5 h-3.5" />
            {tentConfig.hasPrivateBathroom ? messages.privateBathroom : messages.sharedBathroom}
          </span>

          {/* First 2 amenities */}
          {tentConfig.amenities.slice(0, 2).map((amenity, idx) => (
            <span
              key={idx}
              className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600"
            >
              {amenity.summary}
            </span>
          ))}
        </div>

        {/* Quantity selector */}
        {isAvailable && (
          <div className="flex items-center justify-between pt-3 border-t border-dashed border-gray-300">
            <span className="text-sm text-gray-600">Quantity</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onQuantityChange(-1)}
                disabled={quantity === 0}
                className={clsx(
                  'w-9 h-9 rounded-full flex items-center justify-center',
                  'border-2 transition-all duration-150',
                  'focus:outline-none focus:ring-2 focus:ring-offset-1',
                  theme.ctaRing,
                  {
                    [`${theme.accentBorder} ${theme.accentText} hover:${theme.accentBgLight}`]:
                      quantity > 0,
                    'border-gray-200 text-gray-300 cursor-not-allowed': quantity === 0,
                  },
                )}
                aria-label="Decrease quantity"
              >
                <MinusIcon className="w-4 h-4" />
              </button>

              <span
                className={clsx(
                  'w-8 text-center text-lg font-mono font-semibold',
                  quantity > 0 ? theme.accentText : 'text-gray-400',
                )}
              >
                {quantity}
              </span>

              <button
                type="button"
                onClick={() => onQuantityChange(1)}
                className={clsx(
                  'w-9 h-9 rounded-full flex items-center justify-center',
                  'border-2 transition-all duration-150',
                  'focus:outline-none focus:ring-2 focus:ring-offset-1',
                  theme.accentBorder,
                  theme.accentText,
                  theme.ctaRing,
                  `hover:${theme.accentBgLight}`,
                )}
                aria-label="Increase quantity"
              >
                <PlusIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Icon components
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const UsersIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

const BathIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 6h16M4 6v12a2 2 0 002 2h12a2 2 0 002-2V6M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2"
    />
  </svg>
);

const MinusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

export default RoomCard;
